import {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { authContext } from "./authContext";
import {
  getContactsModel,
  getConversationModel,
  getListConversationModel,
  markAsReadConversationModel,
  sendMesageModel,
} from "models/messageModel";
import { XmppContext } from "./xmppContext";
import { notifContext } from "./notifContext";

export const MessageContext = createContext();

export function MessageProvider({ children }) {
  const { user } = useContext(authContext);
  const { newMessage, setUnreadModule, newMessageRead } =
    useContext(notifContext);
  const { sendMessage } = useContext(XmppContext);
  const [listConversation, setListConversation] = useState(null);
  const [listContact, setListContact] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [showListConversation, setShowListConversation] = useState(true);
  const [isLoading, setIsLoading] = useState({
    listConversation: false,
    listContact: false,
    conversation: false,
  });

  const [pageConversation, setPageConversation] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  /** Reset all message-related states */
  const resetMessageContext = useCallback(() => {
    setListConversation([]);
    setConversation(null);
  }, []);

  const mapContact = (jid) =>
    listContact?.find((c) => Number(c.phoneNumber) == Number(jid)) ?? {
      username: jid,
      phoneNumber: jid,
    };
  /** Fetch all contacts */
  const handleGetListContact = useCallback(async () => {
    if (!user) return;
    setIsLoading((p) => ({ ...p, listConversation: true, listContact: true }));
    const response = await getContactsModel(user.phoneNumber);
    if (response.success) setListContact(response.data || []);

    setIsLoading((p) => ({
      ...p,
      listConversation: false,
      listContact: false,
    }));
  }, [user]);

  /** Fetch all conversations */
  const handleGetListConversation = useCallback(async () => {
    if (!listContact) return;
    setIsLoading((p) => ({ ...p, listConversation: true }));

    const response = await getListConversationModel(user.phoneNumber);
    if (response.success) {
      const mapped = response.data?.map((item) => ({
        id: item.id,
        partner: mapContact(item.partnerJid),
        unreadCount: item.unreadCount,
        lastMessage: {
          sender: mapContact(item.lastMessage.senderJid),
          receipent: mapContact(item.lastMessage.receipentJid),
          message: item.lastMessage.message,
          createDate: item.lastMessage.createDate,
        },
      }));

      setListConversation(mapped || []);
    }

    setIsLoading((p) => ({ ...p, listConversation: false }));
  }, [listContact]);

  /** Get single conversation detail */
  const handleGetConversation = async (id, appendUp = false) => {
    if (appendUp && pageConversation > totalPage) return;

    if (!appendUp) setIsLoading((p) => ({ ...p, conversation: true }));
    if (appendUp) setPageConversation(pageConversation + 1);
    else {
      setPageConversation(2);
    }
    const response = await getConversationModel(
      user.phoneNumber,
      id,
      appendUp ? pageConversation : 1,
      20
    );
    if (response.success) {
      const receipent = listContact?.find(
        (c) => Number(c.phoneNumber) == Number(id)
      ) ?? {
        phoneNumber: id,
        username: id,
      };

      setConversation({
        receipent,
        messages: appendUp
          ? conversation?.messages
            ? [...response.data, ...conversation.messages]
            : response.data
          : response.data,
      });

      setTotalPage(response.totalPage);

      if (!appendUp) {
        // Mark conversation as read
        setListConversation((prev) =>
          prev?.map((c) =>
            Number(c.partner.phoneNumber) == Number(id)
              ? { ...c, lastMessage: { ...c.lastMessage, isRead: 1 } }
              : c
          )
        );
      }
    }

    if (!appendUp) setIsLoading((p) => ({ ...p, conversation: false }));
  };

  /** Send a new message (API + XMPP + UI update) */
  const handleSendMessage = async (sender, receipent, messageText) => {
    if (!user?.phoneNumber || !messageText?.trim()) return;

    const newMsg = {
      message: messageText,
      sender,
      receipent,
      unreadCount: 0,
      createDate: new Date().toISOString(),
    };

    const response = await sendMesageModel(
      sender.phoneNumber,
      receipent.phoneNumber,
      messageText
    );

    if (!response.success) return response;

    const msgId = `${response.data.id}`;

    const msgData = {
      senderJid: sender.phoneNumber,
      receipentJid: receipent.phoneNumber,
      message: messageText,
      id: msgId,
      createDate: new Date(),
    };

    setConversation((prev) =>
      prev
        ? {
            ...prev,
            messages: [
              ...(prev.messages?.map((m) => ({
                ...m,
                isRead:
                  Number(m.receipentJid) == Number(user.phoneNumber)
                    ? 1
                    : m.isRead,
              })) || []),
              msgData,
            ],
          }
        : [msgData]
    );

    sendMessage(
      msgId,
      { username: user.username, phoneNumber: user.phoneNumber },
      { username: receipent.username, phoneNumber: receipent.phoneNumber },
      JSON.stringify({
        id: msgId,
        partner: sender,
        lastMessage: newMsg,
      })
    );

    setListConversation((prev) => {
      const exists = prev.find(
        (c) => Number(c.partner.phoneNumber) == Number(receipent.phoneNumber)
      );
      const updated = exists
        ? prev.map((c) =>
            Number(c.partner.phoneNumber) == Number(receipent.phoneNumber)
              ? { ...c, lastMessage: newMsg }
              : c
          )
        : [
            {
              id: response.data.id,
              partner: receipent,
              lastMessage: newMsg,
              unreadCount: 0,
            },
            ...prev,
          ];
      return updated.sort(
        (a, b) =>
          new Date(b.lastMessage.createDate) -
          new Date(a.lastMessage.createDate)
      );
    });

    return response;
  };

  /** Handle incoming messages */
  useEffect(() => {
    if (!newMessage) return;

    const newMessageMappingContact = {
      ...newMessage,
      partner: mapContact(newMessage.partner?.phoneNumber),
      lastMessage: {
        ...newMessage.lastMessage,
        sender: mapContact(newMessage.lastMessage.sender?.phoneNumber),
        receipent: mapContact(newMessage.lastMessage.receipent?.phoneNumber),
      },
    };
    const isInDetailNewMessage =
      Number(conversation?.receipent?.phoneNumber) ==
      Number(newMessage?.partner?.phoneNumber);
    setListConversation((prev) => {
      const exists = prev?.find(
        (c) =>
          Number(c.partner?.phoneNumber) ==
          Number(newMessage.partner?.phoneNumber)
      );

      const updated = exists
        ? prev?.map((item) =>
            Number(item.partner?.phoneNumber) ==
            Number(newMessage.partner?.phoneNumber)
              ? {
                  ...item,
                  partner: newMessageMappingContact.partner,
                  unreadCount: isInDetailNewMessage ? 0 : item.unreadCount + 1,
                  lastMessage: {
                    ...newMessageMappingContact.lastMessage,
                  },
                }
              : item
          )
        : prev
        ? [
            ...prev,
            {
              ...newMessageMappingContact,
              unreadCount: isInDetailNewMessage ? 0 : 1,
            },
          ]
        : [
            {
              ...newMessageMappingContact,
              unreadCount: isInDetailNewMessage ? 0 : 1,
            },
          ];

      return updated.sort(
        (a, b) =>
          new Date(b.lastMessage.createDate) -
          new Date(a.lastMessage.createDate)
      );
    });

    if (isInDetailNewMessage) {
      setConversation((prev) => ({
        ...prev,
        messages: [
          ...(prev.messages || []),
          {
            receipentJid: newMessage.lastMessage.receipent?.phoneNumber,
            senderJid: newMessage.lastMessage.sender?.phoneNumber,
            message: newMessage.lastMessage.message,
            createDate: newMessage.lastMessage.createDate,
            isRead: 0,
          },
        ],
      }));
      setUnreadModule((prev) => ({
        ...prev,
        message: prev?.message?.filter(
          (item) =>
            Number(item.partnerJid) != Number(newMessage?.partner?.phoneNumber)
        ),
      }));
      markAsReadConversationModel(
        conversation?.receipent?.phoneNumber,
        user?.phoneNumber
      );
    }
  }, [newMessage]);

  useEffect(() => {
    if (!newMessageRead) return;
    const isInDetailNewMessage = conversation?.messages?.find(
      (m) => m.id == newMessageRead
    )
      ? true
      : false;

    if (isInDetailNewMessage) {
      setConversation((prev) => ({
        ...prev,
        messages: prev.messages.map((m) => ({
          ...m,
          isRead:
            Number(m.senderJid) == Number(user.phoneNumber) ? 1 : m.isRead,
        })),
      }));
    }
  }, [newMessageRead]);

  /** Initial load: fetch contacts first, then conversations */
  useEffect(() => {
    handleGetListContact();
  }, [handleGetListContact]);

  useEffect(() => {
    handleGetListConversation();
  }, [handleGetListConversation]);

  return (
    <MessageContext.Provider
      value={{
        listConversation,
        setListConversation,
        setPageConversation,
        pageConversation,
        setTotalPage,
        totalPage,
        conversation,
        setConversation,
        resetMessageContext,
        showListConversation,
        setShowListConversation,
        listContact,
        handleSendMessage,
        handleGetListContact,
        handleGetConversation,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
}
